import { useOutletContext } from "react-router-dom";

import { Grid, Stack } from "@mui/material";
import {
  ContentCard,
  FormikAutocomplete,
  FormikErrorNotification,
  FormikTextField,
  FormikYesNoAutocomplete,
  LastModifiedInfo,
  SaveBar,
} from "@shared";

import { CounselorResponse } from "@/api";
import { ReadOnlyField } from "@/common/components/read-only-field/ReadOnlyField";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import formatTimestamp from "@/common/utils/formatTimestamp";
import { formatUserCode } from "@/common/utils/formatUserCode";
import { formatCounselorRoles } from "@/pages/system-settings/tabs/counselors/CounselorList.utils";
import { useTranslation } from "@/translations";

import {
  MAX_MOBILE_NUMBER_LENGTH,
  MAX_PHONE_EXTENSION_LENGTH,
  MAX_USER_CODE_LENGTH,
} from "./CounselorPersonalData.types";
import { useCounselorPersonalData } from "./hooks/useCounselorPersonalData";

const FIELD_GRID_SIZE = { xs: 12, sm: 6 };

export function CounselorPersonalData() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const counselor = useOutletContext<CounselorResponse>();
  const { formik, teamOptions } = useCounselorPersonalData(counselor);

  return (
    <Stack component="form" onSubmit={formik.handleSubmit} sx={{ flex: 1, maxHeight: "100%", gap: 1 }}>
      <FormikErrorNotification formik={formik} />

      <Stack flex={1} gap={1} sx={{ overflowY: "auto" }}>
        <ContentCard title={t("counselors.personalData.portalCard")} grow={false}>
          <Grid container spacing={3}>
            <Grid size={FIELD_GRID_SIZE}>
              <ReadOnlyField label={t("counselors.fields.userId")} value={counselor.fswUserId} />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <ReadOnlyField label={t("counselors.fields.mandantId")} value={counselor.mandantId} />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <ReadOnlyField label={t("counselors.fields.firstName")} value={counselor.firstName} />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <ReadOnlyField label={t("counselors.fields.lastName")} value={counselor.lastName} />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <ReadOnlyField label={t("counselors.fields.email")} value={counselor.email} />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <ReadOnlyField label={t("counselors.fields.roles")} value={formatCounselorRoles(counselor.roles, t)} />
            </Grid>
          </Grid>
        </ContentCard>

        <ContentCard title={t("counselors.personalData.ownCard")} grow={false}>
          <Grid container spacing={2}>
            <Grid size={FIELD_GRID_SIZE}>
              <FormikTextField
                formik={formik}
                fieldName="userCode"
                label={t("counselors.fields.kzz")}
                required
                slotProps={{ input: { inputProps: { maxLength: MAX_USER_CODE_LENGTH } } }}
              />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <FormikAutocomplete
                formik={formik}
                fieldName="team"
                label={t("counselors.fields.team")}
                options={teamOptions}
              />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <FormikYesNoAutocomplete
                formik={formik}
                fieldName="isTeamLeader"
                label={t("counselors.fields.teamLeader")}
                required
              />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <FormikTextField
                formik={formik}
                fieldName="mobileNumber"
                label={t("counselors.fields.mobileNumber")}
                slotProps={{ input: { inputProps: { maxLength: MAX_MOBILE_NUMBER_LENGTH } } }}
              />
            </Grid>
            <Grid size={FIELD_GRID_SIZE}>
              <FormikTextField
                formik={formik}
                fieldName="phoneExtension"
                label={t("counselors.fields.phoneExtension")}
                slotProps={{ input: { inputProps: { maxLength: MAX_PHONE_EXTENSION_LENGTH } } }}
              />
            </Grid>
          </Grid>
        </ContentCard>
      </Stack>

      <SaveBar
        resetLabel={t("common.resetChanges")}
        onReset={formik.handleReset}
        resetDisabled={formik.isSubmitting || !formik.dirty}
        saveLabel={t("common.save")}
        saveDisabled={formik.isSubmitting || !formik.dirty}
        info={
          <LastModifiedInfo
            text={t("common.lastChanged", {
              date: formatTimestamp(counselor.lastModifiedAtUtc, t, language),
              user: formatUserCode(counselor.lastModifiedByUserCode, t),
            })}
          />
        }
      />
    </Stack>
  );
}
